import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Alert, Badge, Card, Container, Modal, Table } from 'react-bootstrap';
import { useAuth } from './auth/AuthContext';

function sortEntries(a, b) {
    const aNo = Number(a?.competitorNo);
    const bNo = Number(b?.competitorNo);
    const aHasNo = Number.isFinite(aNo) && a?.competitorNo != null;
    const bHasNo = Number.isFinite(bNo) && b?.competitorNo != null;

    if (aHasNo && bHasNo && aNo !== bNo) return aNo - bNo;
    if (aHasNo !== bHasNo) return aHasNo ? -1 : 1;

    return String(a.crewName || '').localeCompare(String(b.crewName || ''));
}


export function RegistrationDesk() {
    const { getToken } = useAuth();
    const [entries, setEntries] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [selectedEntry, setSelectedEntry] = useState(null);
    const [showCrewModal, setShowCrewModal] = useState(false);
    const [crewMembers, setCrewMembers] = useState([]);
    const [crewMembersLoading, setCrewMembersLoading] = useState(false);
    const [crewMembersError, setCrewMembersError] = useState('');
    const crewMembersCacheRef = useRef(new Map());

    const buildBroeCrewUrl = (crewId) => {
        const safeId = Number(crewId || 0);
        if (!safeId) return '';
        return `https://broe2.britishrowing.org/CompetitionAdmin/ViewCrew/139?id2=639205344000000000&crewId=${safeId}`;
    };

    const loadCrewMembers = async (crewId) => {
        const safeId = Number(crewId || 0);
        if (!safeId) {
            setCrewMembers([]);
            return;
        }

        const cachedMembers = crewMembersCacheRef.current.get(safeId);
        if (cachedMembers) {
            setCrewMembers(cachedMembers);
            return;
        }

        setCrewMembersLoading(true);
        setCrewMembersError('');

        try {
            const token = getToken();
            const response = await fetch(`/api/competitor/${safeId}`, {
                headers: { Authorization: `Bearer ${token}` }
            });

            if (!response.ok) {
                throw new Error(`Failed to load crew members (${response.status})`);
            }

            const payload = await response.json();
            const members = Array.isArray(payload) ? payload : [];
            crewMembersCacheRef.current.set(safeId, members);
            setCrewMembers(members);
        } catch (err) {
            setCrewMembers([]);
            setCrewMembersError(err.message || 'Failed to load crew members.');
        } finally {
            setCrewMembersLoading(false);
        }
    };

    const handleRowClick = (entry) => {
        setSelectedEntry(entry);
        setShowCrewModal(true);
        setCrewMembers([]);
        setCrewMembersError('');
        loadCrewMembers(entry?.id);
    };

    const handleCloseCrewModal = () => {
        setShowCrewModal(false);
        setSelectedEntry(null);
        setCrewMembers([]);
        setCrewMembersError('');
        setCrewMembersLoading(false);
    };

    useEffect(() => {
        let active = true;

        const loadEntries = async () => {
            setLoading(true);
            setError('');

            try {
                const token = getToken();
                const response = await fetch('/api/registration-desk/entries', {
                    headers: { Authorization: `Bearer ${token}` }
                });

                if (!response.ok) {
                    throw new Error(`Failed to load registration entries (${response.status})`);
                }

                const payload = await response.json();
                if (active) {
                    setEntries(Array.isArray(payload) ? payload : []);
                }
            } catch (err) {
                if (active) {
                    setError(err.message || 'Failed to load registration entries.');
                }
            } finally {
                if (active) {
                    setLoading(false);
                }
            }
        };

        loadEntries();
        return () => {
            active = false;
        };
    }, [getToken]);

    const groupedByClub = useMemo(() => {
        const grouped = new Map();

        for (const entry of entries) {
            const clubName = String(entry.clubName || '').trim() || '(Unknown Club)';
            if (!grouped.has(clubName)) grouped.set(clubName, []);
            grouped.get(clubName).push(entry);
        }

        return [...grouped.entries()]
            .sort((a, b) => a[0].localeCompare(b[0]))
            .map(([clubName, clubEntries]) => ({
                clubName,
                entries: [...clubEntries].sort(sortEntries)
            }));
    }, [entries]);

    const formatSubmittedDate = (value) => {
        if (!value) return '-';

        const text = String(value).trim();
        const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

        const isoMatch = text.match(/^(\d{4})-(\d{2})-(\d{2})/);
        if (isoMatch) {
            const [, y, m, d] = isoMatch;
            const monthIndex = Number(m) - 1;
            const monthName = monthNames[monthIndex] || m;
            return `${Number(d)} ${monthName} ${y}`;
        }

        const ukMatch = text.match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})/);
        if (ukMatch) {
            const [, d, m, y] = ukMatch;
            const monthIndex = Number(m) - 1;
            const monthName = monthNames[monthIndex] || m;
            return `${Number(d)} ${monthName} ${y}`;
        }

        return text;
    };

    if (loading) {
        return <p><em>Loading...</em></p>;
    }

    return (
        <Container fluid>
            <h1>Registration Desk</h1>
            {error && <Alert variant="danger">{error}</Alert>}
            {!error && groupedByClub.length === 0 && <Alert variant="info">No entries found.</Alert>}

            {groupedByClub.map(group => (
                <Card className="mb-3" key={group.clubName}>
                    <Card.Header>
                        <strong>{group.clubName}</strong> ({group.entries.length})
                    </Card.Header>
                    <Card.Body>
                        <Table responsive bordered hover size="sm" className="mb-0">
                            <thead>
                                <tr>
                                    <th style={{ width: '26%' }}>Event</th>
                                    <th style={{ width: '10%' }}>No.</th>
                                    <th style={{ width: '22%' }}>Crew</th>
                                    <th style={{ width: '24%' }}>First Race</th>
                                    <th style={{ width: '10%' }}>Payment</th>
                                    <th style={{ width: '8%' }}>Submitted</th>
                                </tr>
                            </thead>
                            <tbody>
                                {group.entries.map(entry => (
                                    <tr
                                        key={entry.id}
                                        style={{ opacity: entry.statusLabel === 'Scratched' ? 0.65 : 1, cursor: 'pointer' }}
                                        onClick={() => handleRowClick(entry)}
                                    >
                                        <td>{entry.eventName || '-'}</td>
                                        <td>{entry.competitorNo ?? '-'}</td>
                                        <td>{entry.crewName || '-'}</td>
                                        <td style={{ whiteSpace: 'nowrap' }}>
                                            {entry.firstRaceTime ? (
                                                <>
                                                    {entry.firstRaceRaceNo && <strong>R{entry.firstRaceRaceNo} </strong>}
                                                    {entry.firstRaceTime}
                                                    {entry.firstRaceOpp && (
                                                        <> v <span style={entry.firstRaceOppScratched ? { color: '#999', textDecoration: 'line-through' } : undefined}>{entry.firstRaceOpp}</span>{entry.firstRaceOppScratched && <> <Badge bg="warning" text="dark" style={{ fontSize: '0.7em' }}>scr</Badge></>}</>
                                                    )}
                                                </>
                                            ) : '-'}
                                        </td>
                                        <td>
                                            {entry.paid ? (
                                                <span style={{ display: 'inline-block', padding: '0.25em 0.5em', fontSize: '0.75em', fontWeight: 600, lineHeight: 1, borderRadius: '0.375rem', border: '1.5px solid #198754', color: '#198754', background: '#fff' }}>Paid</span>
                                            ) : (
                                                <Badge bg="danger">Not Paid</Badge>
                                            )}
                                        </td>
                                        <td style={{ whiteSpace: 'nowrap' }}>
                                            {formatSubmittedDate(entry.dateSubmitted)}
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </Table>
                    </Card.Body>
                </Card>
            ))}

            <Modal show={showCrewModal} onHide={handleCloseCrewModal} centered>
                <Modal.Header closeButton style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: '1rem' }}>
                    <div style={{ flex: 1 }}>
                        <Modal.Title>
                            {selectedEntry?.crewName || 'Crew'}
                            {selectedEntry?.competitorNo != null ? ` (${selectedEntry.competitorNo})` : ''}
                        </Modal.Title>
                        {selectedEntry?.clubName && <div style={{ fontSize: '0.85em', fontWeight: 'normal', color: '#666', marginTop: '0.25rem' }}>{selectedEntry.clubName}</div>}
                        {selectedEntry?.eventName && <div style={{ fontSize: '0.85em', fontWeight: 'normal', color: '#666', marginTop: '0.1rem' }}>{selectedEntry.eventName}</div>}
                    </div>
                    {selectedEntry && Number(selectedEntry.id || 0) > 0 && (
                        <a
                            href={buildBroeCrewUrl(selectedEntry.id)}
                            target="_blank"
                            rel="noopener noreferrer"
                            aria-label={`Open ${selectedEntry.crewName || 'crew'} in BROE`}
                            title="Open in BROE"
                            style={{ color: '#0d6efd', textDecoration: 'none', flexShrink: 0, marginTop: '0.25rem' }}
                        >
                            <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" fill="currentColor" viewBox="0 0 16 16" aria-hidden="true">
                                <path fillRule="evenodd" d="M14 3.5a.5.5 0 0 0-.5-.5h-4a.5.5 0 0 0 0 1h2.793L7.146 9.146a.5.5 0 1 0 .708.708L13 4.707V7.5a.5.5 0 0 0 1 0z" />
                                <path fillRule="evenodd" d="M2.5 1A1.5 1.5 0 0 0 1 2.5v11A1.5 1.5 0 0 0 2.5 15h11a1.5 1.5 0 0 0 1.5-1.5v-4a.5.5 0 0 0-1 0v4a.5.5 0 0 1-.5.5h-11a.5.5 0 0 1-.5-.5v-11a.5.5 0 0 1 .5-.5h4a.5.5 0 0 0 0-1z" />
                            </svg>
                        </a>
                    )}
                </Modal.Header>
                <Modal.Body>
                    {crewMembersLoading && <p className="mb-0"><em>Loading crew members...</em></p>}
                    {!crewMembersLoading && crewMembersError && <Alert variant="danger" className="mb-0">{crewMembersError}</Alert>}
                    {!crewMembersLoading && !crewMembersError && crewMembers.length === 0 && (
                        <Alert variant="info" className="mb-0">No crew members found.</Alert>
                    )}
                    {!crewMembersLoading && !crewMembersError && crewMembers.length > 0 && (
                        <Table responsive bordered hover size="sm" className="mb-0">
                            <thead>
                                <tr>
                                    <th style={{ width: '20%' }}>Position</th>
                                    <th style={{ width: '55%' }}>Name</th>
                                    <th style={{ width: '25%', whiteSpace: 'nowrap' }}>Membership No.</th>
                                </tr>
                            </thead>
                            <tbody>
                                {crewMembers.map((member, index) => {
                                    const fullName = [member?.firstName, member?.lastName].filter(Boolean).join(' ').trim();
                                    return (
                                        <tr key={`${member?.membershipNo || 'member'}-${index}`}>
                                            <td>{member?.position || '-'}</td>
                                            <td>{fullName || '-'}</td>
                                            <td>{member?.membershipNo || '-'}</td>
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </Table>
                    )}
                </Modal.Body>
            </Modal>
        </Container>
    );
}
